



















import {
  computed,
  defineComponent, onMounted,
  ref,
  useContext, useFetch,
  useRoute, watchPostEffect,
} from '@nuxtjs/composition-api';
import { SfLoader } from '@storefront-ui/vue';
import { useCache } from '@vue-storefront/cache';

import HTMLContent from '~/components/common/HTMLContent.vue';
import { useContent } from '~/composables';
import { useHtmlRouter } from '~/composables/useHtmlRouter';
import { CMS_PAGE_SLAVERY_IDENTIFIER } from '~/constants';
import { getCmsTags } from '~/helpers/cacheHelpers';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { RouteData } from '~/middleware/url-resolver';
import type { CmsPage } from '~/modules/GraphQL/types';
import { usePageStore } from '~/stores/page';

interface CmsProps {
  default?: {
    identifier: string
  }
}

const DYNAMIC_ROUTE_CLASSES = ['slavery-archive'];

export default defineComponent({
  name: 'CmsPage',
  components: {
    HTMLContent,
    SfLoader,
  },
  setup() {
    const { routeData, cmsPage: cmsPageFromStore } = usePageStore();
    const route = useRoute();
    const { error: nuxtError } = useContext();
    const {
      loadPage,
      loading,
      error,
    } = useContent();
    const { addDynamicRoutes } = useHtmlRouter();

    const page = ref<CmsPage | null>(cmsPageFromStore);
    const cmsContent = ref<HTMLElement | null>(null);
    const title = ref<string | null>('');
    const { addTags } = useCache();

    const identifier = computed(() => {
      const cmsProps: CmsProps = typeof route.value.matched[0].props === 'object' ? route.value.matched[0].props : {};

      return routeData?.identifier || cmsProps.default?.identifier;
    });
    const cssClass = computed(() => identifier.value?.replace('.html', '') || '');

    useFetch(async () => {
      if (!identifier.value) {
        return;
      }

      let currentIdentifier = identifier.value;

      if (!page.value) {
        if (identifier.value === CMS_PAGE_SLAVERY_IDENTIFIER) {
          currentIdentifier = route.value.path.replace(/^\//g, '');
        }

        page.value = await loadPage({ identifier: currentIdentifier });
      }

      if (error?.value?.page || !page.value) nuxtError({ statusCode: 404 });

      title.value = page.value?.content_heading || page.value?.title;
    });

    watchPostEffect(
      () => {
        if (cmsContent.value) {
          title.value = cmsContent.value.querySelector('h1')?.textContent || title.value;
        }
      },
    );

    onMounted(() => {
      DYNAMIC_ROUTE_CLASSES.forEach((blockClass) => addDynamicRoutes(`.${blockClass}`));
    });

    addTags(getCmsTags());

    return {
      page,
      loading,
      title,
      cmsContent,
      cssClass,
      routeData,
    };
  },
  head() {
    return getMetaInfo(this.page, false, (this.routeData as RouteData).canonicalUrl, true);
  },
});
